/**
 * useGoalHistory / useGoalSnapshots (Story 18.7 — Historical Goal Tracking)
 *
 * Two read hooks over the 18.7 server contract:
 *
 * - `useGoalHistory(clientId?)` reads GET /api/goals/history (optional
 *   ?clientId= filter), unwrapping the { data: { pastGoals, achievementTrend,
 *   metricImprovement } } envelope. Query key: ["goal-history", clientId ?? "all"].
 * - `useGoalSnapshots(goalId | undefined)` reads GET /api/goals/:goalId/snapshots,
 *   unwrapping { data: { goal, snapshots } }. Query key: ["goal-snapshots", goalId];
 *   `enabled` guard so it does not fire until a goalId is known (the detail-view
 *   chart only fetches once a row is expanded).
 *
 * House pattern: TanStack Query + apiGet + json.data unwrap + enabled guard
 * (mirrors useClientIntegrations / useGoals). No KV/cache layer — these read
 * the first-party D1 tables (client_goals / goal_snapshots), NOT integration
 * metrics, so the Story 4.12 fetchMetricsWithCache mandate (scoped to
 * adapter.fetchMetrics()) does NOT apply.
 */

import { useQuery } from "@tanstack/react-query";
import { apiGet } from "../lib/apiClient";
import type {
  GoalType,
  GoalUnitType,
  GoalPeriodType,
  GoalStatus,
} from "../lib/goalPageDisplay";

/** A past goal's derived outcome (server: services/goal-history.ts). */
export type GoalOutcome = "achieved" | "missed";

/** One row of GET /api/goals/history → pastGoals[]. */
export interface PastGoal {
  id: string;
  name: string;
  clientId: string;
  clientName: string;
  goalType: GoalType;
  metricKey: string | null;
  platform: string | null;
  targetValue: number;
  finalValue: number;
  unit: GoalUnitType;
  currencyCode: string;
  periodType: GoalPeriodType;
  periodStart: number | null;
  periodEnd: number | null;
  outcome: GoalOutcome | null;
}

/** One ascending monthly bucket of the achievement-rate trend. */
export interface AchievementTrendBucket {
  /** UTC "YYYY-MM" of the goals' periodEnd. */
  month: string;
  achieved: number;
  total: number;
  /** achieved/total rounded to 2 decimals (0–1). */
  rate: number;
}

/** Per-(clientId, metricKey) improvement across the two most recent periods. */
export interface MetricImprovement {
  clientId: string;
  metricKey: string;
  platform: string | null;
  previousValue: number;
  latestValue: number;
  /** Percent change rounded to 1 decimal; null when previousValue is 0. */
  changePct: number | null;
}

export interface GoalHistoryPayload {
  pastGoals: PastGoal[];
  achievementTrend: AchievementTrendBucket[];
  metricImprovement: MetricImprovement[];
}

/** The goal header on GET /api/goals/:goalId/snapshots. */
export interface GoalSnapshotHeader {
  id: string;
  name: string;
  targetValue: number;
  unit: GoalUnitType;
  currencyCode: string;
  periodStart: number | null;
  periodEnd: number | null;
  status: GoalStatus;
  goalType: GoalType;
}

/** One snapshot row (snapshotDate is a YYYY-MM-DD string). */
export interface GoalSnapshotPoint {
  snapshotDate: string;
  currentValue: number;
  status: GoalStatus | null;
}

export interface GoalSnapshotsPayload {
  goal: GoalSnapshotHeader;
  snapshots: GoalSnapshotPoint[];
}

const EMPTY_HISTORY: GoalHistoryPayload = {
  pastGoals: [],
  achievementTrend: [],
  metricImprovement: [],
};

/**
 * Tenant-wide goal history (optionally scoped to one client). Returns empty
 * arrays — never undefined — so the History section can render its states
 * (empty card / table / trend) without null guards.
 */
export function useGoalHistory(clientId?: string) {
  return useQuery<GoalHistoryPayload>({
    queryKey: ["goal-history", clientId ?? "all"],
    queryFn: async () => {
      const url = clientId
        ? `/api/goals/history?clientId=${encodeURIComponent(clientId)}`
        : "/api/goals/history";
      const res = await apiGet(url);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail ?? err.title ?? "Failed to load goal history");
      }
      const json = await res.json();
      return (json.data as GoalHistoryPayload) ?? EMPTY_HISTORY;
    },
  });
}

/**
 * One goal's snapshot series (ASC by date) for the detail-view chart. Disabled
 * until a goalId is provided (the chart mounts only inside an expanded row).
 */
export function useGoalSnapshots(goalId: string | undefined) {
  return useQuery<GoalSnapshotsPayload>({
    queryKey: ["goal-snapshots", goalId],
    queryFn: async () => {
      const res = await apiGet(`/api/goals/${goalId}/snapshots`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail ?? err.title ?? "Failed to load snapshots");
      }
      const json = await res.json();
      return json.data as GoalSnapshotsPayload;
    },
    enabled: !!goalId,
  });
}
